import {
    AttachInternals, Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch
} from "@stencil/core";

/**
 * General Input component for multiple types of inputs
 * @slot (default) - custom label content
 * @slot "input" - custom input element (only use when you need to override the default input element)
 */
@Component({
    tag: "ux-input",
    styleUrl: "input.scss",
    shadow: true,
    formAssociated: true
})
export class Input implements ComponentInterface {
    private uniqueInputId = `ux-input-${++id}`;
    private internalInput: HTMLInputElement;

    @Element() host: HTMLUxInputElement;

    @AttachInternals() private internals: ElementInternals;

    /** Label for the input */
    @Prop() label = "";
    /** Type of input */
    @Prop() type: "text" | "number" | "email" | "password" | "search" | "url" | "tel" | "hidden" = "text";
    /** Placeholder for the input */
    @Prop() placeholder: string = undefined;
    /** name attribute for the input */
    @Prop() name = "";
    /** The id attribute of the input element */
    @Prop({mutable: true}) inputId = "";
    /** Disabled attribute for the input */
    @Prop() disabled = false;
    /** required attribute for the input */
    @Prop() required = false;
    /** readonly attribute for the input */
    @Prop() readonly = false;
    /** minlength attribute for the input */
    @Prop() minlength: number = undefined;
    /** maxlength attribute for the input */
    @Prop() maxlength: number = undefined;
    /** min attribute for the input */
    @Prop() min: string = undefined;
    /** max attribute for the input */
    @Prop() max: string = undefined;
    /** step attribute for the input */
    @Prop() step: number = undefined;
    /** Value for the input */
    // eslint-disable-next-line
    @Prop({
        mutable: true
    }) value = "";
    /** spellcheck attribute for the input */
    @Prop() spellcheck: boolean = undefined;
    /** autocapitalize attribute for the input */
    @Prop() autocapitalize: string = undefined;
    /** autocomplete attribute for the input */
    @Prop() autocomplete: string = undefined;
    /** autofocus attribute for the input */
    @Prop() autofocus = false;
    /** inputmode attribute for the input */
    @Prop() inputmode: "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url" = undefined;
    /** pattern attribute for the input */
    @Prop() pattern: string = undefined;
    /** show error styles for a validation error */
    @Prop() invalid = false;

    /** Emitted when a keyboard input occurred. */
    @Event() uxInput: EventEmitter<string>;
    /** Emitted when the value has changed. */
    @Event() uxInputChange: EventEmitter<string>;
    /** Emitted when the input loses focus. */
    @Event() uxBlur: EventEmitter<FocusEvent>;
    /** Emitted when the input has focus. */
    @Event() uxFocus: EventEmitter<FocusEvent>;

    /** Indicates if the component has focus or not */
    @State() hasFocus = false;

    /** Returns the native input element */
    @Method()
    async getInputElement(): Promise<HTMLInputElement> {
        return this.internalInput;
    }

    /** Set the focus on the input element */
    @Method()
    async setFocus() {
        this.internalInput.focus();
    }

    /** Remove the focus from the input element */
    @Method()
    async removeFocus() {
        this.internalInput.blur();
    }

    @Watch("inputId")
    private setId() {
        if (this.inputId === "") {
            this.inputId = this.uniqueInputId;
        }
    }

    private handleFocus = (e: FocusEvent) => {
        this.hasFocus = true;
        this.uxFocus.emit(e);
    };

    private handleBlur = (event: FocusEvent) => {
        // only emit blur if the whole component loses focus
        if (!this.host.contains(event.relatedTarget as HTMLElement)) {
            this.hasFocus = false;
            this.uxBlur.emit();
        }
    };

    private handleInput = async () => {
        this.value = this.internalInput.value;
        this.uxInput.emit(this.value);
        this.internals.setFormValue(this.value);
    };

    private handleChange = async () => {
        this.value = this.internalInput.value;
        this.uxInputChange.emit(this.value);
        this.internals.setFormValue(this.value);
    };

    componentWillLoad(): void {
        this.setId();
        this.internals.setFormValue(this.value);
    }

    render() {
        return (
            <Host class={{
                "is-focused": this.hasFocus
            }}>
                <label htmlFor={this.inputId}>
                    {this.label}<slot/> {this.required ? <span class='required'>*</span> : null}
                </label>
                <div><div class='controls'>
                    <input
                        ref={(el) => (this.internalInput = el)}
                        type={this.type}
                        placeholder={this.placeholder}
                        name={this.name}
                        id={this.inputId}
                        disabled={this.disabled}
                        required={this.required}
                        readonly={this.readonly}
                        minlength={this.minlength}
                        maxlength={this.maxlength}
                        max={this.max}
                        min={this.min}
                        step={this.step}
                        value={this.value}
                        spellcheck={this.spellcheck}
                        autocapitalize={this.autocapitalize}
                        autocomplete={this.autocomplete}
                        autofocus={this.autofocus}
                        inputmode={this.inputmode}
                        pattern={this.pattern}
                        onInput={() => this.handleInput()}
                        onChange={() => this.handleChange()}
                        onBlur={this.handleBlur}
                        onFocus={this.handleFocus}
                    />
                </div></div>
            </Host>
        );
    }
}

let id = 0;
